<template>
    <div id="app" class="min-w-[1024px] min-h-[100vh] flex">
        <AppAside />
        <AppMain :header-name="titleText">
            <div class="min-w-7xl flex-1 mt-9 flex flex-col">
                <div class="px-10 pt-10 rounded-02 w-full max-w-7xl 2xl:w-3/4 bg-background-basic-1 border border-content-basic-4">
                    <div class="flex flex-col gap-2">
                        <div class="font-inter-heading-18 text-content-basic-1">Sub-admin</div>
                        <div class="flex flex-col items-start gap-1">
                            <ElementsSearchBar
                                v-model="employeeNameForSearch"
                                :options="employeeOptions"
                                :optionClickEvent="false"
                                :hasSearchButton="true"
                                :height12="true"
                                :width60="true"
                                :placeholder="'Search Employees'"
                                @searchEvent="onSearchEmployee"
                                @update:modelValue="onSelectFromList"
                                ref="employeeSearchBar"
                            />
                            <div class="mt-2 font-inter-caption-2 text-accent-red-3" v-if="subAdminError.subAdmin">
                                {{ subAdminError.subAdmin }}
                            </div>
                        </div>
                    </div>

                    <!-- Department to Assign section -->
                    <div class="mt-10">
                        <div class="flex flex-col items-start gap-1 mb-4">
                            <p class="font-inter-heading-18 text-content-basic-1">Department to Assign ({{ assignedDepartmentCount }})</p>
                            <p class="font-inter-caption-2 text-accent-red-3" v-if="subAdminError.assignedDepartmentList">
                                {{ subAdminError.assignedDepartmentList }}
                            </p>
                        </div>
                        <DepartmentTreeSelector
                            :selectedSeqs="departmentSeqs"
                            :disabledSeqs="otherAssignedDepartmentSeqs"
                            @clickDepartment="toggleAssignDepartment"
                        />
                    </div>

                    <div class="mt-6 -mx-10 px-10 flex justify-end items-center gap-3 py-6 border-t">
                        <DesignButton class="w-24" btnType="QUATERNARY" btnSize="MD" :btnDisabled="false" btnText="Cancel" @click-event="onCancel" />
                        <DesignButton class="w-24" btnType="PRIMARY" btnSize="MD" :btnDisabled="false" :btnText="primaryButtonText" @click-event="onSubmit" />
                    </div>
                </div>
            </div>
        </AppMain>
    </div>

    <Teleport to="body">
        <EmployeeSelectPopup
            v-model="showEmployeeSelectPopup" 
            :employeeList="employeeList" 
            @save-employee="afterSelectEmployee" 
        />
    </Teleport>
</template>

<style scoped>
.blu { background: #4361ee; }
.blu:hover { background: #334cba; }
</style>

<script>
import AppAside from '@/components/AppAside.vue';
import AppMain from '@/components/main/AppMain.vue';
import ElementsInput from '@/components/elements/ElementsInput.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import ElementsSearchBar from '@/components/elements/ElementsSearchBar.vue';
import EmployeeSelectPopup from '@/components/employee/employee-select-popup/employee-select-popup';
import DepartmentTreeSelector from '@/pages/flex_ben/flex_ben_setting/subAdmin/components/DepartmentTreeSelector.vue';


export default {
    name: 'SubAdminAddEditForm',
    components: { 
        AppAside,
        AppMain, 
        ElementsInput, 
        ElementsSearchBar,
        EmployeeSelectPopup, 
        DepartmentTreeSelector,  
        DesignButton,
    },
    data() {
        return {
            subAdminSeq: null,
            subAdmin: {
                employeeSeq: null,
                subAdminName: null,
                assignedDepartmentList: [],
            },
            departmentSeqs: [],
            otherAssignedDepartmentSeqs: [],
            employeeNameForSearch: null,
            employeeOptions: [],
            employeeList: [],
            showEmployeeSelectPopup: false,
            subAdminError: {
                subAdmin: null,
                assignedDepartmentList: null,
            },

            showDepartmentError: false,
            
            scrollYBackup: 0
        };
    },
    computed: {
        isEdit() { return !!this.subAdminSeq; },
        titleText() { return this.isEdit ? 'Edit Sub-admin' : 'Add Sub-admin'; },
        primaryButtonText() { return this.isEdit ? 'Save' : 'Add'; },
        assignedDepartmentCount() { return this.departmentSeqs.length; },
        isAnyPopupOpen() {
            return this.showEmployeeSelectPopup;
        },
    },
    watch: {
        isAnyPopupOpen(val) {
            if (val) {
                this.lockBodyScroll();
            } else {
                this.unlockBodyScroll();
            }
        },
    },
    mounted() {
        const url = new URL(window.location);
        this.subAdminSeq = url.searchParams.get('subAdminSeq') ?? null;
        if (this.isEdit) {
            this.getSubAdmin();
        }
        this.getEmployeeOptions();
        this.getOtherAssignedDepartments();
    },
    methods: {
        lockBodyScroll() {
            // 현재 스크롤 위치 저장
            this.scrollYBackup = window.scrollY || document.documentElement.scrollTop || 0;

            // 레이아웃 점프 방지 및 스크롤 잠금
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this.scrollYBackup}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden'; // 보조(데스크톱)
        },
        unlockBodyScroll() {
            // 원복
            const y = this.scrollYBackup || 0;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflow = '';

            // 저장해 둔 위치로 복귀
            window.scrollTo(0, y);
        },
        initSubAdmin() {
            const self = this;
            self.subAdmin.employeeSeq = null;
            self.subAdmin.subAdminName = null;
            self.employeeNameForSearch = null;
        },
        getEmployeeOptions() {
            const url = this.$api('uri', 'get-employee-exclude-sub-admin');
            const json_query = JSON.stringify({
                limit: -1,
                offset: 0,
            });

            this.$axios
                .get(url, { params: { json_query } })
                .then((res) => {
                    this.employeeList = res.data.data.list;
                    this.employeeOptions = this.employeeList.map((emp) => ({ text: emp.employeeName, value: emp.employeeSeq }));
                })
                .catch((err) => {
                    console.error('err : ', err);
                });
        },
        getOtherAssignedDepartments() {
            const self = this;
            const url = self.$api('uri', 'get-other-assigned-departments').replace('{subAdminSeq}', self.subAdminSeq);
            self.$axios.get(url).then((res) => {
                self.otherAssignedDepartmentSeqs = res.data.data.list.map(d => d.departmentSeq);
            });
        },
        getSubAdmin() {
            const self = this;
            const url = self.$api('uri', 'get-sub-admin').replace('{subAdminSeq}', self.subAdminSeq);

            self.$axios
                .get(url)
                .then((res) => {
                    self.subAdmin = res.data.data;
                    self.employeeNameForSearch = self.subAdmin.subAdminName;
                    self.departmentSeqs = res.data.data.assignedDepartmentList.map(dep => dep.departmentSeq);
                })
                .catch((err) => {
                    console.error('err ? : ', err);
                });
        },
        addSubAdmin(payload) {
            const url = this.$api('uri', 'post-sub-admin');
            this.$axios.post(url, payload).then(() => {
                this.redirectToSubAdminSetting();
            });
        },
        updateSubAdmin(payload) {
            const url = this.$api('uri', 'put-sub-admin').replace('{subAdminSeq}', this.subAdminSeq);
            this.$axios.put(url, payload).then(() => {
                this.redirectToSubAdminSetting();
            });
        },
        validateSubAdmin(payload) {  
            const self = this;
            self.initSubAdminError();

            let isError = false;
            if (!payload.employeeSeq) {
                self.subAdminError.subAdmin = 'Please select Employee to proceed.';
                isError = true;
            }

            if (payload.departmentSeqs.length === 0) {
                self.subAdminError.assignedDepartmentList = 'Please assign at least 1 department. ';
                isError = true;
            }

            return isError;
        },
        initSubAdminError() {
            const self = this;
            self.subAdminError.subAdmin = null;
            self.subAdminError.assignedDepartmentList = null;
        },
        onSubmit() {
            const self = this;
            const payload = {
                employeeSeq: self.subAdmin.employeeSeq,
                departmentSeqs: self.departmentSeqs,
            };

            const isError = self.validateSubAdmin(payload);
            if (isError) {
                return;
            }

            if (self.isEdit) {
                self.updateSubAdmin(payload);
            } else {
                self.addSubAdmin(payload);
            }
        },
        onSearchEmployee() {
            this.showEmployeeSelectPopup = true;
        },
        onSelectFromList(label, option = null) {
            if (!option && this.subAdmin.employeeSeq && this.subAdmin.subAdminName === label) {
                return;
            }

            const selectedEmployee = this.employeeList.find(e => String(e.employeeSeq) === String(option?.value));
            if (!selectedEmployee) {
                this.initSubAdmin();
                return;
            }

            this.subAdmin.employeeSeq = selectedEmployee.employeeSeq;
            this.subAdmin.subAdminName = selectedEmployee.employeeName;
            this.employeeNameForSearch = selectedEmployee.employeeName;
        },
        toggleAssignDepartment(departmentSeq) {
            this.departmentSeqs = this.departmentSeqs.includes(departmentSeq)
            ? this.departmentSeqs.filter(s => s !== departmentSeq)
            : [...this.departmentSeqs, departmentSeq];
        },
        onCancel() {
            window.history.back();
        },
        afterSelectEmployee(employee) {
            const self = this;

            if (!employee) return;
            self.subAdmin.employeeSeq = employee.employeeSeq;
            self.subAdmin.subAdminName = employee.employeeName;
            self.employeeNameForSearch = employee.employeeName;
            self.$nextTick(() => self.$refs.employeeSearchBar?.setInputText(employee.employeeName));
            self.showEmployeeSelectPopup = false;
        },
        redirectToSubAdminSetting() {
            // shell인 경우 shell 페이지로 이동, 아닌 경우 기존 페이지로 이동
            const path = this.$appUtil.checkPermission('FLEXBEN_EDIT_SHELL')
                ? '/flex_ben_shell/flex_ben_setting'
                : '/flex_ben/flex_ben_setting';
            window.location.href = `${path}?tab=subAdminSetting`;
        },
        
    },
};
</script>


